/* eslint-disable import/no-named-as-default-member */
/* eslint-disable camelcase */

// node modules
import React, { Component, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import PropTypes from 'prop-types';
import { connect, Provider, ReactReduxContext } from 'react-redux';
import withStyles from 'isomorphic-style-loader/withStyles';
import StyleContext from 'isomorphic-style-loader/StyleContext';
import { stringify } from 'qs';

// app modules: actions
import {
  openTableOfContents,
  closeTableOfContents,
  setSelectedTableOfContents,
} from 'actions/tableOfContents';
import { loadSearchView } from 'actions/search';

// app modules: components
import Advert from 'components/Advert';
import AlacartePromo from 'components/AlacartePromo';
import AlacarteStatusIndicator from 'components/AlacarteStatusIndicator';
import AuthorBooks from 'components/AuthorBooks';
import BlogPosts from 'components/BlogPosts';
import BookRankOverlay from 'components/BookRankOverlay';
import Button from 'components/Button';
import BookStatus from 'components/BookStatus';
import BookAwards from 'components/BookAwards';
import BuyBookButton from 'components/Book/BuyBookButton';
import CategoryPills from 'components/CategoryPills';
import ContentOptions from 'components/ContentOptions';
import AddToCollectionButton from 'components/AddToCollectionButton';
import EditorialBlurb from 'components/EditorialBlurb';
import FavouriteButton from 'components/FavouriteButton';
import Heading from 'components/Heading';
import HeadContent from 'components/HeadContent';
import Icon from 'components/Icon';
import Image from 'components/Image';
import TopRecipes from 'components/TopRecipes';
import Recommendations from 'components/Recommendations';
import RelatedBooks from 'components/RelatedBooks';
import { SearchInBook } from 'components/SearchIn';
import withFeatureToggle from 'components/FeatureToggleHoC';

// app modules: props
import RecommendationsProps from 'components/Recommendations/RecommendationsProps';

// app modules: services
import analytics from 'services/analytics';
import assets from 'services/assets';
import formatter from 'services/formatter';
import history from 'services/history';
import route, { booksUrl } from 'services/route';
import { isApp } from 'services/environment';
import { openWindow } from 'services/inAppBrowser';
import bookProgress from 'services/bookProgress';

// assets
import CssBook from './Book.scss';
import { CONTENT_TYPES, COLOURS } from '../../constants';

const TableOfContents = React.lazy(() =>
  import(/* webpackChunkName: 'table-of-contents' */ 'components/TableOfContents'),
);

class Book extends Component {
  static propTypes = {
    alacarteEnabled: PropTypes.bool.isRequired,
    baseUrl: PropTypes.string.isRequired,
    userId: PropTypes.number,
    book: PropTypes.shape({
      alacarte: PropTypes.bool,
      alacarte_available: PropTypes.bool,
      authors: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          first_name: PropTypes.string,
          last_name: PropTypes.string,
          avatar_name: PropTypes.string,
          is_author: PropTypes.bool,
        }),
      ),
      signature_: PropTypes.string,
      title: PropTypes.string,
      title_short: PropTypes.string,
      indexable: PropTypes.bool,
      isbn: PropTypes.string,
      image_name: PropTypes.string,
      image_permission: PropTypes.bool,
      licensed: PropTypes.bool,
      publication_date: PropTypes.string,
      publisher_main: PropTypes.string,
      publisher_blurb: PropTypes.string,
      ckbk_blurb: PropTypes.string,
      coming_soon: PropTypes.bool,
      ranking_recommendations: PropTypes.number,
      recommendations: PropTypes.arrayOf(RecommendationsProps.recommendation),
      awards: PropTypes.arrayOf(
        PropTypes.shape({
          result: PropTypes.string,
          name: PropTypes.string,
        }),
      ),
      free_scan_url: PropTypes.string,
      advertising_opt_out: PropTypes.bool,
      categories: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          title: PropTypes.string,
          type: PropTypes.string,
        }),
      ),
      hardcopy_link: PropTypes.shape({
        url: PropTypes.string,
        service: PropTypes.string,
      }),
    }),
    tableOfContents: PropTypes.shape({
      isOpen: PropTypes.bool,
    }).isRequired,
    openTableOfContents: PropTypes.func.isRequired,
    closeTableOfContents: PropTypes.func.isRequired,
    loadSearchView: PropTypes.func.isRequired,
    setSelectedTableOfContents: PropTypes.func.isRequired,
    debug: PropTypes.bool,
    environment: PropTypes.string.isRequired,
    forceSsr: PropTypes.bool,
  };

  static defaultProps = {
    book: {
      alacarte: false,
      title: 'Missing',
      indexable: false,
      isbn: 'missing',
      image_name: 'default',
      publication_date: 'missing',
      publisher_main: 'missing',
      authors: [],
      recommendations: [],
      free_scan_url: null,
      awards: [
        {
          result: '',
          name: '',
        },
      ],
      hardcopy_link: null,
    },
    debug: false,
    userId: null,
    forceSsr: false,
  };

  constructor(props) {
    super(props);

    this.bound = {
      toggleTableOfContents: this.toggleTableOfContents.bind(this),
      searchBookRecipes: this.searchBookRecipes.bind(this),
    };

    this.state = {
      shouldShowSideAdvert: false,
    };

    this.advertRef = React.createRef();
    if (typeof window !== 'undefined' && window.IntersectionObserver) {
      this.advertObserver = new IntersectionObserver(this.onAdvertMove);
    }
  }

  componentDidMount() {
    const { book, userId } = this.props;

    this.props.setSelectedTableOfContents({
      id: null,
      chapter: null,
      book,
    });

    analytics.sendEvent({
      event: 'Content.Book.View',
      id: book.isbn,
      book: book.title,
    });
  }

  componentDidUpdate(prevProps) {
    const { book } = this.props;
    if (this.bookHasChanged(prevProps)) {
      this.props.setSelectedTableOfContents({
        id: null,
        chapter: null,
        book,
      });
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ shouldShowSideAdvert: false });
    }

    // Keep an eye on the position of the sidebar advert slot
    if (this.advertObserver) {
      this.advertObserver.disconnect();
      this.advertObserver.observe(this.advertRef.current);
    }
  }

  componentWillUnmount() {
    if (this.advertObserver) {
      this.advertObserver.disconnect();
    }
  }

  onAdvertMove = () => {
    // Only display the side advert if its more than 500px from the top and the document is long
    // So that we don't end up making the page too dense with ads.
    // This will depend on how many author-books load (async) above it so we will use
    // IntersectionObserver to only display the advert if it passes the y=500 threshold.
    const { shouldShowSideAdvert } = this.state;
    if (
      !shouldShowSideAdvert &&
      this.advertRef.current?.getBoundingClientRect().top > 600 &&
      document.body.clientHeight > 2000
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ shouldShowSideAdvert: true });
      this.advertObserver.disconnect();
    }
  };

  getAllRecipesLink = () => {
    const { title } = this.props.book;
    const queryParams = stringify(
      { q: 'recipes', 'book_full_title[0]': title },
      { encodeValuesOnly: true },
    );
    return `/search?${queryParams}`;
  };

  toggleTableOfContents({ store, insertCss }) {
    const { book, userId, tableOfContents } = this.props;

    const isRedirected = bookProgress.getBackToLastSection(book.isbn, userId);

    if (isRedirected) {
      return;
    }

    if (tableOfContents.isOpen) {
      this.props.closeTableOfContents();
    } else {
      const root = createRoot(document.getElementById('modal'));
      root.render(
        <StyleContext.Provider value={{ insertCss }}>
          <Provider store={store}>
            <Suspense fallback={null}>
              <TableOfContents />
            </Suspense>
          </Provider>
        </StyleContext.Provider>,
      );

      this.props.openTableOfContents();
    }
  }

  bookHasChanged(prevProps) {
    const { book } = this.props;

    return book.isbn !== prevProps.book.isbn;
  }

  searchBookRecipes(event) {
    if (event) {
      event.preventDefault();
    }
    this.props.loadSearchView();
    setTimeout(
      () => history.push(this.getAllRecipesLink(), { referrer: 'Book', section: 'Search' }),
      0,
    );
  }

  canShowContent() {
    const { indexable, licensed, alacarte } = this.props.book;
    const { alacarteEnabled } = this.props;

    if (indexable && licensed) {
      if (alacarte && !alacarteEnabled) {
        return false;
      }
      return true;
    }
    return false;
  }

  renderAlsoBy() {
    const { book } = this.props;

    return book.authors.map((author, authorIndex) => {
      const key = `also-by-${authorIndex}`;
      const authorName = `${author.first_name} ${author.last_name}`;
      return (
        <AuthorBooks
          key={key}
          authorId={author.id}
          heading={`Also by ${authorName}`}
          exclude={[book.isbn]}
        />
      );
    });
  }

  renderSearch() {
    const { title } = this.props.book;

    if (this.canShowContent()) {
      return <SearchInBook title={title} className="margin-bottom-40" />;
    }

    return null;
  }

  renderRecommendations() {
    const { recommendations } = this.props.book;

    if (recommendations.length > 0) {
      return (
        <section>
          <Heading withDash className="margin-bottom-20">
            Recommended by
          </Heading>
          <Recommendations recommendations={recommendations} />
        </section>
      );
    }

    return null;
  }

  renderFreeBookButton() {
    const { free_scan_url } = this.props.book;
    if (free_scan_url) {
      const caption = 'Free eBook';
      return (
        <Button
          onClick={() => {
            if (isApp()) {
              openWindow(free_scan_url, '_blank');
            } else {
              window.location = free_scan_url;
            }
          }}
          target="_system"
          themeType="clear"
          themeName="energized"
          size="large"
          title={caption}
          className="book__action-button"
        >
          <Icon fill={COLOURS.ENERGIZED} name="archive_org" height="20" width="20" mr={5} />
          <span className="book__action-text">{caption}</span>
        </Button>
      );
    }
    return null;
  }

  renderContentsButton() {
    if (this.canShowContent()) {
      return (
        <StyleContext.Consumer>
          {({ insertCss }) => (
            <ReactReduxContext.Consumer>
              {({ store }) => (
                <Button
                  onClick={() => this.bound.toggleTableOfContents({ store, insertCss })}
                  themeType="clear"
                  themeName="energized"
                  size="large"
                  title="Show table of contents"
                  className="book__action-button"
                >
                  <Icon fill={COLOURS.ENERGIZED} name="list" height="20" width="20" mr={5} />
                  Contents
                </Button>
              )}
            </ReactReduxContext.Consumer>
          )}
        </StyleContext.Consumer>
      );
    }

    return null;
  }

  renderRecipesButton() {
    if (this.canShowContent()) {
      return (
        <Button
          href={this.getAllRecipesLink()}
          onClick={this.bound.searchBookRecipes}
          themeType="clear"
          themeName="energized"
          size="large"
          title="Recipes in this book"
          className="book__action-button"
        >
          <Icon fill={COLOURS.ENERGIZED} name="grid" height="14" width="14" mr={5} />
          Recipes
        </Button>
      );
    }

    return null;
  }

  renderWhyNot() {
    const { indexable, licensed, coming_soon } = this.props.book;

    if (!indexable || !licensed) {
      let explainer;
      if (!licensed || coming_soon === false) {
        explainer = (
          <p className="margin-bottom-5">
            We are building our collection of cookbooks all the time. This book is on our wish list,
            but it is not yet available on ckbk.
          </p>
        );
      } else {
        explainer = (
          <p className="margin-bottom-5">
            This book has been licensed, and will soon be available on ckbk. New books are converted
            and uploaded each month.
          </p>
        );
      }
      return (
        <div className="book__box margin-bottom-20">
          <p className="book__boxheader">Why aren’t the recipes for this book available on ckbk?</p>
          {explainer}
          <p className="margin-bottom-5">
            Books which are part of ckbk&apos;s collection show one of these two logos:
          </p>
          <ul className="margin-left-20">
            <li>
              <img
                src="images/ckbk-book.png"
                width="33"
                alt="now available on ckbk"
                className="book__explainer-image"
              />{' '}
              now available on ckbk
            </li>
            <li>
              <img
                src="images/ckbk-soon-book.png"
                width="33"
                alt="coming soon to ckbk"
                className="book__explainer-image"
              />{' '}
              coming soon to ckbk
            </li>
          </ul>
        </div>
      );
    }
    return null;
  }

  render() {
    const { shouldShowSideAdvert } = this.state;
    const { alacarteEnabled, forceSsr } = this.props;
    const {
      authors,
      alacarte,
      alacarte_available,
      awards,
      ckbk_blurb,
      categories,
      free_scan_url,
      hardcopy_link,
      image_name,
      indexable,
      isbn,
      licensed,
      publication_date,
      publisher_blurb,
      publisher_main,
      ranking_recommendations,
      signature_,
      title,
    } = this.props.book;

    const { debug, environment, baseUrl } = this.props;

    const doNotDisplayStatusIcon =
      (!indexable && !licensed) ||
      (alacarte && alacarte_available) ||
      (!alacarteEnabled && alacarte);
    const image = assets.coverUrl(image_name, {
      resize: 'fit-in',
      width: 315,
      height: 315,
    });
    const srcSet = assets.srcSet(
      image_name,
      [{ w: 315, h: 315, resize: 'fit-in' }, { w: 630, h: 630, resize: 'fit-in' }],
      assets.coverUrl,
    );

    const COVER_IMAGE_SIZE = 315;
    const people = authors.map(author => ({
      '@type': 'Person',
      name: `${author.first_name} ${author.last_name}`,
    }));
    const currentPath = route.book(isbn, signature_);

    const bookData = {
      '@context': 'http://schema.org',
      '@type': 'Book',
      name: title,
      author: people,
      url: currentPath,
    };
    return (
      <div className="book">
        <HeadContent
          contents={{
            content: image,
            width: COVER_IMAGE_SIZE,
            height: COVER_IMAGE_SIZE,
            structuredData: bookData,
            url: currentPath,
          }}
        />

        <div className="book__main">
          {!doNotDisplayStatusIcon ? (
            <div className="book__status">
              <BookStatus statusType="medium" book={this.props.book} />
            </div>
          ) : null}

          <header className="book__header">
            <div className="book__header__cover">
              {alacarte ? (
                <AlacarteStatusIndicator
                  available={alacarte_available}
                  isbn={isbn}
                  signature_={signature_}
                  className="book__header__alacarte-badge"
                />
              ) : null}
              <Image
                alt={title}
                height={315}
                image={image}
                overlay={
                  <BookRankOverlay ranking={ranking_recommendations} size="medium" withLink />
                }
                srcSet={srcSet}
              />
            </div>
            <div className="book__header__image-buttons">
              <FavouriteButton
                contentId={isbn}
                contentType={CONTENT_TYPES.BOOK}
                hash={formatter.contentHash.generate([title])}
              />

              <AddToCollectionButton itemId={isbn} itemType={CONTENT_TYPES.BOOK} />
              <ContentOptions
                heading={title}
                textToShare={`${title} by ${formatter.authorsList.toString(authors)} on ckbk`}
                urlToShare={baseUrl + currentPath}
                isbn={isbn}
                itemType={CONTENT_TYPES.BOOK}
                subMenu="share"
              >
                <Icon
                  fill={COLOURS.ENERGIZED}
                  name="native-share"
                  width="20"
                  style={{ marginTop: -3 }}
                />
              </ContentOptions>
            </div>

            <Heading level={1} className="heading--size-32">
              {title}
            </Heading>
            <p className="book__header__authors">
              by <b>{formatter.authorsList.toDOMWithLinks(authors)}</b>
            </p>
          </header>
          <div className="book__actions margin-bottom-20">
            <BuyBookButton
              alacarte={alacarte}
              compact={!!free_scan_url}
              indexable={indexable}
              isbn={isbn}
              service={hardcopy_link?.service}
              signature_={signature_}
              url={hardcopy_link?.url}
            />
            {this.renderFreeBookButton()}
            {this.renderRecipesButton()}
            {this.renderContentsButton()}
          </div>
          <EditorialBlurb publisherBlurb={publisher_blurb} ckbkBlurb={ckbk_blurb} />
          {this.canShowContent() ? (
            <TopRecipes
              isbn={isbn}
              onViewMore={this.bound.searchBookRecipes}
              viewMoreHref={this.getAllRecipesLink()}
              className="book__top-recipes margin-bottom-20"
              forceSsr={forceSsr}
            />
          ) : null}
          {this.renderSearch()}
          {alacarte ? <AlacartePromo isbn={isbn} className="book__alc-promo" /> : null}
          {this.renderWhyNot()}
          <div className="book__header">
            <div className="book__categories">
              <CategoryPills
                backText="Book"
                categories={categories?.map(category => {
                  let tab;
                  switch (category.type) {
                    case 'country':
                      tab = 'countries';
                      break;
                    case 'continent':
                      tab = 'continents';
                      break;
                    case 'tag':
                      tab = 'tags';
                      break;
                    case 'region':
                      tab = 'regions';
                      break;
                    case 'collection':
                      tab = 'collections';
                      break;
                    default:
                      tab = 'categories';
                  }
                  return { ...category, href: booksUrl({ tab, category: category.id }) };
                })}
              />
            </div>
            <div className="book__details">
              <dl>
                <dt>Original Publisher</dt>
                <dd>
                  <strong>{publisher_main}</strong>
                </dd>
              </dl>

              <dl>
                <dt>Date of publication</dt>
                <dd>
                  <strong>{publication_date}</strong>
                </dd>
              </dl>

              <dl>
                <dt>ISBN</dt>
                <dd>
                  <strong>{isbn}</strong>
                </dd>
              </dl>
            </div>
          </div>
          <BlogPosts
            className="margin-bottom-40"
            headingLevel={2}
            headingWithDash
            hideIfEmpty
            itemProps={{ allCapsTitle: false, withGutter: true }}
            isbn={isbn}
            limit={10}
            showMoreButton={false}
            skeletonItems={0}
          />

          {this.renderRecommendations()}
          {/* Disabling book-book recommendations for now, except in testing */}
          {environment.toLowerCase() === 'beta' ? null : <RelatedBooks isbn={isbn} />}
          {debug ? <RelatedBooks isbn={isbn} variant="combined" /> : null}
          {debug ? <RelatedBooks isbn={isbn} variant="favourite" /> : null}
          {debug ? <RelatedBooks isbn={isbn} variant="recommendation" /> : null}
        </div>

        <div className="book__aside">
          <BookAwards awards={awards} />
          {this.renderAlsoBy()}
          {/* We measure the location of this div to decide whether to render a side advert */}
          <div ref={this.advertRef} />
          {shouldShowSideAdvert ? (
            <Advert
              className="margin-bottom-20 margin-top-40"
              slotName="book-aside"
              format="vertical"
              fullWidthResponsive={false}
              hideOnMobile
              ezoicPlaceholderId={104}
              lazy
            />
          ) : null}
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  tableOfContents: state.tableOfContents,
  environment: state.config.environment,
  baseUrl: state.config.baseUrl,
  userId: state.user.id,
});

const mapDispatchToProps = {
  openTableOfContents,
  closeTableOfContents,
  setSelectedTableOfContents,
  loadSearchView,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(CssBook)(withFeatureToggle(Book, 'alacarte')));
